import { defineStore } from 'pinia';
import { ref } from 'vue';
import { browserNotifications } from '@/services/BrowserNotifications';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  deploymentRequestId?: number;
  duration?: number;
}

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([]);
  const browserNotificationsEnabled = ref(browserNotifications.isEnabled);

  function addToast(toast: Omit<Toast, 'id'>) {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000
    };

    toasts.value.push(newToast);

    // Send browser notification if page is not visible
    browserNotifications.notify({
      title: toast.title,
      message: toast.message,
      type: toast.type,
      tag: toast.deploymentRequestId?.toString()
    });

    // Auto-remove after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }

    return id;
  }

  function removeToast(id: string) {
    const index = toasts.value.findIndex(t => t.id === id);
    if (index > -1) {
      toasts.value.splice(index, 1);
    }
  }

  function clearAll() {
    toasts.value = [];
  }

  function success(title: string, message: string, deploymentRequestId?: number) {
    return addToast({ type: 'success', title, message, deploymentRequestId });
  }

  function error(title: string, message: string, deploymentRequestId?: number) {
    return addToast({ type: 'error', title, message, deploymentRequestId, duration: 8000 });
  }

  function info(title: string, message: string, deploymentRequestId?: number) {
    return addToast({ type: 'info', title, message, deploymentRequestId });
  }

  function warning(title: string, message: string, deploymentRequestId?: number) {
    return addToast({ type: 'warning', title, message, deploymentRequestId });
  }

  async function requestNotificationPermission(): Promise<boolean> {
    const granted = await browserNotifications.requestPermission();
    browserNotificationsEnabled.value = browserNotifications.isEnabled;
    return granted;
  }

  function getNotificationPermission(): NotificationPermission {
    return browserNotifications.permission;
  }

  return {
    toasts,
    browserNotificationsEnabled,
    addToast,
    removeToast,
    clearAll,
    success,
    error,
    info,
    warning,
    requestNotificationPermission,
    getNotificationPermission
  };
});
